import { memo, useEffect } from "react";
import {
  GalleryData,
  InputProps,
  Links,
  ProjectDataProps,
  ProjectFormComponetProps,
  SelectOption,
} from "../../../common/types";
import { ErrorMessage, inputContainerStyle } from "../contactForm";
import { styled } from "styled-components";
import { SingleValue } from "react-select";
import {
  handleProjectFormBlur,
  ProjectvalidateField,
} from "../../../../utils/Validations/adminFormValidationUtil";
import { useFileManager } from "../../../../hooks/useFileManager";

const PreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 20px 0;
  gap: 10px;
  div {
    width: 150px;
    height: 200px;
    background-color: ${({ theme }) => theme.skillSetBg};
    border-radius: 5px;
    padding: 3px;

    position: relative;
    // cursor: pointer;
    display: flex;

    img {
      border-radius: 5px;
      width: 100%;
      height: auto;
      object-fit: cover;
    }

    video {
      width: 100%;
      height: auto;
      border-radius: 5px;
      object-fit: cover;
    }
  }
  .add-description {
    position: absolute;
    text-align: center;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    background: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 12px;
    padding: 2px 5px;
    border-radius: 3px;
  }

  .removeButton {
    position: absolute;
    top: 6px;
    right: 6px;
    background: rgba(255, 0, 0, 0.1);
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-size: 15px;

    &:hover {
      background: rgba(255, 0, 0, 1);
    }
    &:focus {
      background: darkred;
    }
  }
`;

const EditingModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 998;

  .modal-content {
    background-color: white;
    color: black;
    padding: 20px;
    border-radius: 8px;
    width: 300px;
    text-align: center;
    font-size: 14px;

    label {
      font-weight: 500;
    }

    input {
      margin: 5px 0;
      padding: 8px 4px;
      border: 1px solid #ccc;
      border-radius: 5px;
      outline: none;

      &:hover {
        border: 1px solid #aaa;
      }
      &:focus {
        border: 1px solid #000;
      }
    }

    button {
      border: none;
      padding: 8px 0;
      border-radius: 5px;
      background-color: #ddd;

      &:hover {
        background-color: #ccc;
      }
      &:focus {
        background-color: #bbb;
      }
    }

    img,
    video {
      border-radius: 4px;
      max-width: 100%;
      margin-bottom: 10px;
    }
  }
`;

const UnifiedFileInput = styled.div<InputProps>`
  position: relative;
  border: 2px solid ${({ theme, $errorColor }) => $errorColor || theme.text};
  border-radius: 2px;
  padding: 40px 20px;
  text-align: center;
  margin: 10px 0;
  transition: all 0.3s;
  cursor: pointer;
  color: ${({ theme }) => theme.text};

  &:hover {
    background: ${({ theme }) => theme.circularIconBg};
    color: ${({ theme }) => theme.circularIconTextBg};
  }

  &.active {
    border: 2px dotted red;
    color: ${({ theme }) => theme.circularIconTextBg};
    background: ${({ theme }) => theme.circularIconBg};
  }

  input[type="file"] {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  .input-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 110px;
    gap: 10px;

    svg {
      font-size: 2rem;
    }

    p {
      margin: 0;
      font-size: 1rem;
    }

    .browse-text {
      text-decoration: underline;
    }
  }
`;

const DraggableItem = styled.div<{ $isDragging: boolean; $isCover: boolean }>`
  position: relative;
  cursor: grab;
  transition: all 0.2s ease-out;
  overflow: hidden;
  /* Force hardware acceleration for smoother transitions */
  will-change: transform, opacity;

  /* Cover image styling */
  border: ${({ $isCover, theme }) => 
    $isCover ? `2px solid ${theme.text}` : '2px solid transparent'};
  box-shadow: ${({ $isCover }) => 
    $isCover ? '0 0 0 3px rgba(0, 100, 255, 0.3)' : 'none'};

  /* Dragging state */
  opacity: ${({ $isDragging }) => ($isDragging ? 0.7 : 1)};
  transform: ${({ $isDragging }) => 
    $isDragging ? 'scale(1.05) rotate(2deg)' : 'scale(1) rotate(0deg)'};
  box-shadow: ${({ $isDragging }) => 
    $isDragging ? '0 10px 20px rgba(0,0,0,0.2)' : '0 2px 5px rgba(0,0,0,0.1)'};
  z-index: ${({ $isDragging }) => ($isDragging ? 10 : 1)};

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }

  /* Cover badge */
  &::after {
    content: '${({ $isCover }) => $isCover ? 'Cover' : ''}';
    position: absolute;
    top: 8px;
    left: 8px;
     background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: bold;
    z-index: 2;
  }
`;

const FilesInput: React.FC<ProjectFormComponetProps> = ({
  projectData,
  errors,
  touched,
  setProjectData,
  setErrors,
  setTouched,
  showAlert,
  isFormSubmitted,
  setIsFormSubmitted,
}) => {
  const {
    galleryData,
    selectedFiles,
    isDragging,
    editingItem,
    dragState,
    setSelectedFiles,
    openEditor,
    closeEditor,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    addFiles,
    removeFile,
    updateDescription,
  } = useFileManager(projectData.galleryData);

  // Handle file input change
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      //handle files change and selection
      const result = addFiles(event.target.files);

      if (result) {
        //Validate on file change
        validateFieldWrapper("galleryData", result.updatedGalleryData);

        //Alert duplicates
        if (result.hasDuplicates) {
          showAlert(
            false,
            "Duplicate Selection",
            "Some files were duplicates and weren't added",
            false
          );
        }
      }

      event.target.value = ""; //reset the input to allow reselection of files
    }
  };

  // Sync with parent component
  useEffect(() => {
    setProjectData((prev) => ({
      ...prev,
      galleryData,
    }));
  }, [galleryData, setProjectData]);

  useEffect(() => {
    if (isFormSubmitted) {
      setSelectedFiles([]); // Reset files on form submission
      if (setIsFormSubmitted) {
        setTimeout(() => setIsFormSubmitted(false), 0); // ✅ Ensure the state resets
      }
    }
  }, [isFormSubmitted, setIsFormSubmitted, setSelectedFiles]);

const handleRemove = (id: string, isImage: boolean) => {
  const result = removeFile(id, isImage);
  validateFieldWrapper("galleryData", result.updatedGalleryData);
  // Additional logic if needed
};


  //validations functions
  const handleBlurWrapper = (
    name: keyof ProjectDataProps,
    value: GalleryData
  ) => {
    handleProjectFormBlur(
      name,
      value,
      touched,
      setTouched,
      validateFieldWrapper
    );
  };

  const validateFieldWrapper = (
    name: keyof ProjectDataProps,
    value:
      | string
      | SingleValue<SelectOption>
      | number
      | GalleryData
      | Partial<Links>
  ) => {
    ProjectvalidateField(name, value, errors, setErrors, projectData);
  };

  return (
    <>
      <div style={inputContainerStyle}>
        <label htmlFor="galleryFiles">
          Upload Images/Videos ({selectedFiles.length} selected)
        </label>
        {/* Unified input, both select and drag and drop */}
        <UnifiedFileInput
          className={isDragging ? "active" : ""}
          onDragEnter={(e: any) => handleDragEnter(e)}
          onDragLeave={(e: any) => handleDragLeave(e)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e: any) => handleDrop(e)}
          onBlur={() => {
            handleBlurWrapper("galleryData", projectData.galleryData);
          }}
          $errorColor={errors.galleryData ? "red" : ""}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              document.getElementById("galleryFiles")?.click();
            }
          }}
          tabIndex={0}
        >
          <input
            type="file"
            id="galleryFiles"
            name="galleryFiles"
            multiple
            accept="image/*,video/*"
            onChange={handleFileChange}
            onBlur={() => {
              handleBlurWrapper("galleryData", projectData.galleryData);
            }}
          />
          <div className="input-label">
            {isDragging ? (
              "Drop files here"
            ) : (
              <>
                {/* <FiUploadCloud size={24} /> */}
                <p>
                  Drag & drop files here or{" "}
                  <span className="browse-text">browse</span>
                </p>
                <div style={{ fontWeight: 200 }}>
                  <small>Supports images and videos</small> <br />
                  {selectedFiles.length > 0 && (
                    <small>{selectedFiles.length} files selected</small>
                  )}
                </div>
              </>
            )}
          </div>
        </UnifiedFileInput>

        {errors.galleryData && (
          <ErrorMessage>{errors.galleryData}</ErrorMessage>
        )}
      </div>

      {(galleryData.imgData.length > 0 || galleryData.vidData.length > 0) && (
        <div>
          <label style={{ fontWeight: 200 }}>Preview</label>
          <p style={{ fontWeight: 200 }}>
            Drag to reorder. First image will be used as project cover.
          </p>
          <PreviewContainer>
            {galleryData.imgData.map((img, index) => (
              <DraggableItem
                key={img.id}
                draggable
                $isDragging={
                  dragState.index === index && dragState.type === "image"
                }
                $isCover={index === 0}
                onDragStart={(e) => handleDragStart(e, index, true)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index, true)}
                onTouchStart={(e) => handleTouchStart(e, index)}
                onTouchMove={(e) => handleTouchMove(e, index, true)}
                onTouchEnd={handleTouchEnd}
                onClick={() => openEditor(img.id, true)}
              >
                <img src={img.src} alt={img.alt} />
                <button
                  className="removeButton"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(img.id, true);
                  }}
                >
                  &#10005;
                </button>
                {!img.description && (
                  <p className="add-description">Add Description</p>
                )}
              </DraggableItem>
            ))}

            {galleryData.vidData.map((vid, index) => (
              <DraggableItem
                key={vid.id}
                draggable
                $isDragging={
                  dragState.index === index && dragState.type === "video"
                }
                $isCover={false}
                onDragStart={(e) => handleDragStart(e, index, false)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index, false)}
                onTouchStart={(e) => handleTouchStart(e, index)}
                onTouchMove={(e) => handleTouchMove(e, index, false)}
                onTouchEnd={handleTouchEnd}
                onClick={() => openEditor(vid.id, false)}
              >
                <video src={vid.src} controls />
                <button
                  className="removeButton"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(vid.id, false);
                  }}
                >
                  &#10005;
                </button>
                {!vid.description && (
                  <p className="add-description">Add Description</p>
                )}
              </DraggableItem>
            ))}
          </PreviewContainer>
        </div>
      )}

      {editingItem && (
        <EditingModal>
          <div className="modal-content">
            <label>Add Description</label>
            {editingItem.isImage ? (
              <img
                src={
                  projectData.galleryData.imgData.find(
                    (img) => img.id === editingItem.id
                  )?.src
                }
                alt={
                  projectData.galleryData.imgData.find(
                    (img) => img.id === editingItem.id
                  )?.alt || "Preview"
                }
              />
            ) : (
              <video
                src={
                  projectData.galleryData.vidData.find(
                    (vid) => vid.id === editingItem.id
                  )?.src
                }
                controls
              />
            )}
            <input
              type="text"
              placeholder="Add description"
              value={
                editingItem.isImage
                  ? galleryData.imgData.find((img) => img.id === editingItem.id)
                      ?.description || ""
                  : galleryData.vidData.find((vid) => vid.id === editingItem.id)
                      ?.description || ""
              }
              onChange={(e) =>
                updateDescription(
                  editingItem.id,
                  "description",
                  e.target.value,
                  editingItem.isImage
                )
              }
            />
            <button onClick={closeEditor}>Close</button>
          </div>
        </EditingModal>
      )}
    </>
  );
};

export default memo(FilesInput);
